import { allWidgets } from './registry';
import { BACKEND_MODULE, GATE } from './gates';
import type {
  ModuleId, TaskRequirement, WidgetAccess, WidgetDefinition,
} from './types';

/**
 * The gate that says the reader has the module: its main endpoint's, or its backend module's top task.
 * `insights` and `links` have none: one reads the other modules, the other holds the transfer certificates.
 */
export const MODULE_GATES: Record<ModuleId, TaskRequirement[]> = {
  insights: [],
  'cash-position': [GATE.cashAccounts],
  investments: [GATE.investments],
  debts: [GATE.debts],
  'cash-flow': [GATE.cashFlows],
  transactions: [GATE.transactions],
  transfers: [GATE.cashPooling],
  closing: [GATE.financialClosing],
  tariffs: [GATE.tariffs],
  policies: [GATE.policies],
  reports: [GATE.reports],
  links: [],
  fx: [BACKEND_MODULE.CURRENCY_RATES],
};

/**
 * Modules that are built but kept off the overview.
 *
 * `insights` reads whatever the other modules expose, which makes it the one
 * module that says nothing a reader could not get from the module it borrowed
 * from. It stays in the code — the widgets work — and out of every tab, the
 * catalogue and the list of modules a tab can be built from, until it earns
 * its place back.
 */
export const HIDDEN_MODULES: ReadonlySet<ModuleId> = new Set<ModuleId>(['insights']);

/** The gates this widget needs opened: its module's and its own. */
export const widgetGates = (definition: WidgetDefinition): TaskRequirement[] =>
  [...MODULE_GATES[definition.module], ...(definition.requiresTasks ?? [])];

export const opensGate = (gate: TaskRequirement, access: WidgetAccess): boolean =>
  gate.some((taskId) => access.has(taskId));

export const allowsWidget = (definition: WidgetDefinition, access: WidgetAccess): boolean =>
  !HIDDEN_MODULES.has(definition.module) && widgetGates(definition).every((gate) => opensGate(gate, access));

/** A module is offered while the reader can open at least one of its widgets. */
export const allowsModule = (module: ModuleId, access: WidgetAccess): boolean =>
  allWidgets().some((definition) => definition.module === module && allowsWidget(definition, access));

/**
 * The catalogue as this reader sees it. Unknown access (still loading, or an
 * endpoint that failed) yields everything: a reader who briefly sees a widget
 * they cannot open is a smaller harm than an overview that silently loses half
 * its panels whenever `/tasks` is slow. This only decides what is *offered*:
 * the overview's slots mount no widget before the access is known
 * (`ViewPanel`'s `pending`).
 */
export const offeredWidgets = (access: WidgetAccess | undefined): WidgetDefinition[] => (
  access
    ? allWidgets().filter((definition) => allowsWidget(definition, access))
    : allWidgets().filter((definition) => !HIDDEN_MODULES.has(definition.module))
);
